import { useEffect, useState } from "react";

import type { OpeningHours } from "../api/types";
import { useLanguage } from "../i18n/LanguageContext";
import { openState } from "../lib/hours";
import "./OpenBadge.css";

/**
 * "Open now · until 23:00" / "Closed · opens tomorrow at 09:00", computed on
 * the visitor's clock and refreshed once a minute.
 */
export default function OpenBadge({ hours, tone = "dark" }: { hours: OpeningHours; tone?: "dark" | "light" }) {
  const { t } = useLanguage();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const state = openState(hours, now);

  let detail: string;
  if (state.isOpen) {
    detail = t("closesAt", { time: state.time });
  } else {
    const day =
      state.daysAhead === 0 ? t("today") : state.daysAhead === 1 ? t("tomorrow") : t(state.nextDay);
    detail = state.time ? t("opensAt", { day, time: state.time }) : "";
  }

  return (
    <span className={`open-badge open-badge-${tone}${state.isOpen ? " is-open" : ""}`} role="status">
      <span className="open-dot" aria-hidden="true" />
      <span className="open-label">{state.isOpen ? t("openNow") : t("closedNow")}</span>
      {detail && <span className="open-detail nums">· {detail}</span>}
    </span>
  );
}
